import { DropdownSelect, SelectItem, Tooltip } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import intersectionWith from 'lodash/intersectionWith';
import { useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import {
  getCSATLanguages,
  SupportedCSATLanguageCode,
} from 'app/views/layout/views/settings/views/satisfaction/utils/supportedCSATLanguages/supportedCSATLanguages';
import { satisfactionActions } from 'components/conversation/actions/satisfaction';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

const defaultLanguage = 'en';

type ConversationHeaderCsatDropdownProps = {
  /** Will disable the dropdown and show this text in the button when present. */
  disabledText?: string;
};

export const ConversationHeaderCsatDropdown = ({
  disabledText,
}: ConversationHeaderCsatDropdownProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const supportedLanguages = useMemo(() => getCSATLanguages(), []);

  const [
    { initial_channel: channel, attributes, id, direction },
    languageCodes = [] as SupportedCSATLanguageCode[],
    asyncStatus,
  ] = useAppSelector(
    (state) => [
      state.conversation.metaConversation.conversation,
      state.conversation.availableSatisfactionLanguages,
      state.conversation.asyncStatus.availableSatisfactionLanguages,
    ],
    shallowEqual,
  );

  const hasSatisfactionAccess = useHasAccess('settings.satisfaction');

  const contactPoint = useMemo(() => {
    if (channel === 'email' || channel === 'contactform') {
      return attributes?.integration_email;
    }
    if (channel == 'pstnphone') {
      if (direction == 'outbound') {
        return attributes?.calling_number;
      }
      return attributes?.called_number;
    }
    if (channel == 'callback') {
      attributes?.callback_number;
    }
    return undefined;
  }, [attributes, channel]);

  useEffect(() => {
    if (channel && contactPoint) {
      bindActionToPromise(
        dispatch,
        satisfactionActions.getAvailableSatisfactionLanguagesRequest,
      )({ channel, contactPoint });
    }
  }, [dispatch, channel, contactPoint]);

  const languageCodeItems = useMemo(() => {
    const availableLanguages = intersectionWith(
      supportedLanguages,
      languageCodes,
      (a, b) => a.code === b,
    );
    return availableLanguages.map(({ code, label }) => ({
      id: code,
      name: label,
      description: code,
    }));
  }, [supportedLanguages, languageCodes]);

  const isCancellable = channel === 'email' || channel === 'contactform';

  const selectedLanguage: SupportedCSATLanguageCode | undefined =
    useMemo(() => {
      if (
        attributes?.language &&
        languageCodes.includes(attributes.language as SupportedCSATLanguageCode)
      ) {
        return attributes.language as SupportedCSATLanguageCode;
      }
      if (languageCodes.includes(defaultLanguage)) {
        return defaultLanguage;
      }
      return undefined;
    }, [languageCodes, attributes?.language]);

  const handleLanguageSelect = ({ id: languageCode }) => {
    ampli.conversationCsatMenuLanguageSelected({
      languageCode,
    });
    bindActionToPromise(
      dispatch,
      satisfactionActions.changeSatisfactionLanguageRequest,
    )({ csid: id, language: languageCode });
  };

  const handleCancelCsat = () => {
    ampli.conversationCsatMenuCancelOptionClicked();
    bindActionToPromise(
      dispatch,
      satisfactionActions.cancelSatisfactionRequest,
    )({ csid: id });
  };

  return (
    <Tooltip disabled={!!disabledText} content="CSAT - Customer Satisfaction">
      <DropdownSelect
        buttonProps={{
          icon: 'smile',
          disabled: !!disabledText,
          isLoading: asyncStatus === 'loading',
          variant: 'tertiary',
        }}
        items={languageCodeItems}
        itemComponent={SelectItem}
        selectedItems={selectedLanguage ? [selectedLanguage] : []}
        onSelect={handleLanguageSelect}
        onClear={
          hasSatisfactionAccess && isCancellable ? handleCancelCsat : undefined
        }
        onClearText="Cancel CSAT"
        onToggle={(isOpen) => {
          if (isOpen) {
            ampli.conversationCsatMenuOpened();
          }
        }}
      >
        {disabledText || selectedLanguage}
      </DropdownSelect>
    </Tooltip>
  );
};
